import { BaseHttpClient } from "../../http/base-http-client";
import type {
  User,
  UserPublic,
  UpdateUserRequest,
  DeleteAccountResponse,
  Organization,
} from "../../types/api/user-types";

/**
 * User API Client
 *
 * Handles all user management endpoints:
 * - User profile operations (get, update, delete)
 * - User search functionality
 *
 * Follows SRP: Single responsibility for user management operations
 * Follows SOC: Separated from business logic and UI concerns
 */
export class UserApiClient extends BaseHttpClient {
  /**
   * Get current user profile
   * @returns Promise<User> - Current user information including organization and subscription details
   */
  async getMe(): Promise<User> {
    return this.get<User>("users/me");
  }

  /**
   * Update current user profile
   * @param data - User update information including name, password, or organization
   * @returns Promise<User> - Updated user information
   */
  async updateMe(data: UpdateUserRequest): Promise<User> {
    return this.put<User>("users/me", data);
  }

  /**
   * Delete current user account
   * This action is permanent and will remove all user data
   * @returns Promise<DeleteAccountResponse> - Confirmation of account deletion
   */
  async deleteAccount(): Promise<DeleteAccountResponse> {
    return this.delete<DeleteAccountResponse>("users/me");
  }

  /**
   * Search users by email or name
   * Used primarily for inviting members to agents
   * @param query - Search query string (minimum 2 characters)
   * @returns Promise<UserPublic[]> - Array of matching users with public information only
   */
  async searchUsers(query: string): Promise<UserPublic[]> {
    return this.get<UserPublic[]>("users/search", { query });
  }

  /**
   * Get the public organization
   * Used to determine if a user is in the public organization for UI display
   * @returns Promise<Organization> - Public organization information
   */
  async getPublicOrganization(): Promise<Organization> {
    return this.get<Organization>("organizations/public");
  }
}

/**
 * Create a default UserApiClient instance
 * @returns UserApiClient - Configured user API client
 */
export function createUserApiClient(): UserApiClient {
  return new UserApiClient();
}

/**
 * Default UserApiClient instance for immediate use
 */
export const userApiClient = createUserApiClient();
